/**
 * AgeGate custom element for age verification modal.
 * Handles focus trapping, validation, and accessibility for age gating.
 *
 * @class AgeGate
 * @extends HTMLElement
 */
class AgeGate extends HTMLElement {
    /**
     * Initializes the AgeGate component, sets up event listeners and focus trap.
     */
    constructor() {
        super();

        this.checkSessionStorage();
        // button selectors
        this.querySelector('button[name="decline"]')?.addEventListener('click', this.reject.bind(this));
        this.querySelector('button[name="accept"]')?.addEventListener('click', this.accept.bind(this));

        // Error message selectors
        this.errorMessage = this.querySelector('.age-gate-popup-modal__error-message');
        this.invalidDateMessage = this.querySelector('.age-gate-popup-modal__invalid-date-message');

        // date picker selectors
        const day = this.querySelector('#day');
        const month = this.querySelector('#month');
        const year = this.querySelector('#year');
        day?.addEventListener('change', this.hideErrorMessage.bind(this));
        month?.addEventListener('change', this.hideErrorMessage.bind(this));
        year?.addEventListener('change', this.hideErrorMessage.bind(this));
        this.enableModalFocusTrap();
    }

    /**
     * Checks sessionStorage for age verification and removes modal if verified.
     * Also disables the focus trap.
     */
    checkSessionStorage() {
        let ageVerified = sessionStorage.getItem('ageVerified');
        if (ageVerified == 'true') document.querySelector('.age-gate-popup-modal').remove();
        document.body.classList.remove('age');
        this.disableModalFocusTrap();
    }

    /**
     * Handles acceptance of age gate. Validates age if date picker is enabled.
     * Sets sessionStorage and dispatches a custom event on success.
     */
    accept() {
        const deactivate = () => {
            sessionStorage.setItem('ageVerified', 'true');
            this.checkSessionStorage();
            document.dispatchEvent(new CustomEvent('age-gate:deactivated'));
        }
        if (this.classList.contains('date-picker--enabled')) {
            if (this.validateAge()) {
                deactivate();
            }
        } else {
            deactivate();
        }

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('login-drawer') !== 'open') return;
        if (window.customerVariables.isCustomer) return;

        const loginDrawer = document.querySelector('login-drawer');
        if (loginDrawer) {
            loginDrawer.open();
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('login-drawer');
            window.history.replaceState({}, '', newUrl.toString());
        }
    }

    /**
     * Handles rejection of age gate. Redirects to Google.
     */
    reject() {
        window.location = 'https://www.google.com';
    }

    /**
     * Validates the user's input for age verification.
     * Checks for empty fields, valid date, and age >= 18.
     *
     * @returns {boolean} True if valid, false otherwise.
     */
    validateAge() {
        const day = this.querySelector('#day');
        const month = this.querySelector('#month');
        const year = this.querySelector('#year');

        // Hide both error messages initially
        this.errorMessage.classList.remove('global-message--active');
        this.invalidDateMessage?.classList.remove('global-message--active');

        // 1. Check for empty fields
        if (!day.value || !month.value || !year.value) {
            this.errorMessage.classList.add('global-message--active');
            return false;
        }

        // 2. Check for valid date
        const months = [
            "january", "february", "march", "april", "may", "june",
            "july", "august", "september", "october", "november", "december"
        ];
        const dayNum = parseInt(day.value, 10);
        const monthNum = months.indexOf(month.value.toLowerCase());
        const yearNum = parseInt(year.value, 10);

        if (
            isNaN(dayNum) || isNaN(monthNum) || monthNum === -1 || isNaN(yearNum)
        ) {
            this.invalidDateMessage?.classList.add('global-message--active');
            return false;
        }

        const dateOfBirth = new Date(yearNum, monthNum, dayNum);
        if (
            dateOfBirth.getFullYear() !== yearNum ||
            dateOfBirth.getMonth() !== monthNum ||
            dateOfBirth.getDate() !== dayNum
        ) {
            this.invalidDateMessage?.classList.add('global-message--active');
            return false;
        }

        // 3. Check if user is at least 18
        const today = new Date();
        const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
        if (dateOfBirth > eighteenYearsAgo) {
            this.errorMessage.classList.add('global-message--active');
            return false;
        }

        return true;
    }

    /**
     * Hides all error messages for the age gate form.
     */
    hideErrorMessage() {
        this.errorMessage.classList.remove('global-message--active');
        this.invalidDateMessage?.classList.remove('global-message--active');
    }

    /**
     * Enables focus trap within the modal dialog for accessibility.
     */
    enableModalFocusTrap() {
        const focusableSelectors = [
            'a[href]',
            'area[href]',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            'iframe',
            'object',
            'embed',
            'audio[controls]',
            'video[controls]',
            '[contenteditable]:not([contenteditable="false"])',
            '[tabindex]:not([tabindex="-1"])',
            'summary'
        ];
        const modal = this.closest('.age-gate-popup-modal');
        const focusableEls = modal.querySelectorAll(focusableSelectors.join(','));
        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];

        this._focusHandler = (e) => {
            if (e.key !== 'Tab') return;
            if (e.shiftKey) {
                if (document.activeElement === firstEl) {
                    e.preventDefault();
                    lastEl.focus();
                }
            } else {
                if (document.activeElement === lastEl) {
                    e.preventDefault();
                    firstEl.focus();
                }
            }
        };
        modal.addEventListener('keydown', this._focusHandler);
        // Focus first element
        firstEl?.focus();
    }

    /**
     * Disables the modal focus trap event handler.
     */
    disableModalFocusTrap() {
        const modal = this.closest('.age-gate-popup-modal');
        if (this._focusHandler) {
            modal.removeEventListener('keydown', this._focusHandler);
            this._focusHandler = null;
        }
    }
}

customElements.define('age-gate', AgeGate);
